// Accepting an invitation (D52): the link an admin shared, `/invite/<token>`, lets the
// invited person choose their password once; then they sign in. No session is needed, and
// every failure (unknown, used, expired) reads the same. Accepting shows the one-time device
// code the person links their phone with (docs/07 "Enrolment and activation").
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import { Button, Input, Password } from "rizzui";

import type { Schemas } from "../api/client";
import { ApiError } from "../api/queries";
import { acceptInvitation } from "../auth/admin";
import { passwordProblem } from "../auth/api";
import { formatDateTime, messages, useT } from "../i18n";
import { LanguageMenu } from "../ui/LanguageMenu";
import { ErrorState } from "../ui/states";
import { SignInLayout } from "./SignInLayout";

type Accepted = Schemas["InvitationAccepted"];

function failure(e: unknown): string {
  const { auth } = messages();
  if (e instanceof ApiError && e.status === 429) return auth.signIn.tooManyAttempts;
  if (e instanceof ApiError && e.status === 403) return auth.invite.invalid;
  return e instanceof Error ? e.message : String(e);
}

export default function InvitePage() {
  const t = useT().auth.invite;
  const { token = "" } = useParams();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState<Accepted>();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const problem = passwordProblem(password, repeat);
    if (problem) return setError(problem);
    setBusy(true);
    setError(undefined);
    try {
      setAccepted(await acceptInvitation(token, password));
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  }

  if (accepted) {
    const code = accepted.device_code;
    return (
      <SignInLayout actions={<LanguageMenu />}>
        <h1 className="text-[28px] tracking-tight lg:text-[32px]">{t.doneTitle}</h1>
        <p className="mt-2 text-[14px] text-gray-500">{t.doneLead(<span className="font-medium text-gray-800">{accepted.email}</span>)}</p>
        {code ? (
          <section aria-label={t.deviceCode} className="mt-6 space-y-3">
            <h2 className="text-[17px]">{t.linkPhone}</h2>
            <p className="m-0 text-[13px] text-gray-500">{t.linkPhoneHelp(formatDateTime(code.expires_at))}</p>
            <Input label={t.oneTimeCode} readOnly value={code.code} onFocus={(e) => e.target.select()} inputClassName="font-mono" />
          </section>
        ) : null}
        <Link to="/sign-in" className="mt-6 inline-block font-semibold text-primary">
          {t.signIn}
        </Link>
      </SignInLayout>
    );
  }

  return (
    <SignInLayout actions={<LanguageMenu />}>
      <h1 className="text-[28px] tracking-tight lg:text-[32px]">{t.title}</h1>
      <p className="mb-0 mt-2 text-[14px] text-gray-500">{t.lead}</p>
      <form className="mt-8 space-y-5" aria-label={t.form} onSubmit={(e) => void onSubmit(e)}>
        <Password size="lg" label={t.password} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <Password size="lg" label={t.repeat} autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
        {error ? <ErrorState title={t.notAccepted} message={error} /> : null}
        <Button type="submit" size="lg" className="w-full" isLoading={busy}>
          {t.submit}
        </Button>
      </form>
    </SignInLayout>
  );
}
